import { Stack, Typography, useTheme } from "@mui/material";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";
import DatePickerComponent from "./DatePickerComponent";
import DescriptionField from "./DescriptionField";
import { RefObject } from "react";
import IconCircleWrapper from "../../../ui/IconCircleWrapper";
import { CaretRightIcon } from "../../../ui/icons";

interface Wallet {
  title: string;
  id: string;
  wallet: {
    id: string;
    name: string;
    balance: number;
    type: string;
  };
}

interface TransferTransactionFormProps {
  transferSourceWallet: Wallet | null;
  selectDestinationWallet: Wallet | null;
  datePicker: Dayjs | undefined;
  description: string;
  openDatePicker: boolean;
  setOpenDialogSelectWallets: (open: boolean) => void;
  setOpenDialogSelectDestinationWallets: (open: boolean) => void;
  setOpenDatePicker: (open: boolean) => void;
  setDescription: (description: string) => void;
  setDatePicker: (date: Dayjs | undefined) => void;
  descriptionInputRef?: RefObject<HTMLInputElement>;
  sourceWalletLabel?: string;
  destinationWalletLabel?: string;
}

const TransferTransactionForm = ({
  transferSourceWallet,
  selectDestinationWallet,
  datePicker,
  description,
  openDatePicker,
  setOpenDialogSelectWallets,
  setOpenDialogSelectDestinationWallets,
  setOpenDatePicker,
  setDescription,
  setDatePicker,
  descriptionInputRef,
  sourceWalletLabel,
  destinationWalletLabel
}: TransferTransactionFormProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'transactions']);

  const sourceLabel = sourceWalletLabel || t('transfer_wallet', { ns: 'transactions' });
  const destLabel = destinationWalletLabel || t('destination_wallet', { ns: 'transactions' });

  return (
    <Stack spacing={4} width="100%">
      {/* source wallet */}
      <Stack
        direction="row"
        spacing={theme.spacing(2)}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
          <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
              <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
            </IconCircleWrapper>
        </Stack>
        {transferSourceWallet ? (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
          >
            <Typography variant="body1" color="secondary">
              {sourceLabel}
            </Typography>
            <Typography
              variant="h3"
              onClick={() => setOpenDialogSelectWallets(true)}
              sx={{ cursor: 'pointer' }}
            >
              {transferSourceWallet.wallet.name}
            </Typography>
          </Stack>
        ) : (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectWallets(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {sourceLabel}
            </Typography>
            <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
          </Stack>
        )}
      </Stack>

      {/* destination wallet */}
      <Stack
        direction="row"
        spacing={theme.spacing(2)}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
          <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
              <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
            </IconCircleWrapper>
        </Stack>
        {selectDestinationWallet ? (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
          >
            <Typography variant="body1" color="secondary">
              {destLabel}
            </Typography>
            <Typography
              variant="h3"
              onClick={() => setOpenDialogSelectDestinationWallets(true)}
              sx={{ cursor: 'pointer' }}
            >
              {selectDestinationWallet.wallet.name}
            </Typography>
          </Stack>
        ) : (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectDestinationWallets(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {destLabel}
            </Typography>
            <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
          </Stack>
        )}
      </Stack>

      {/* Date */}
      <DatePickerComponent
        datePicker={datePicker}
        setDatePicker={setDatePicker}
        openDatePicker={openDatePicker}
        setOpenDatePicker={setOpenDatePicker}
        clearable={false}
      />

      <DescriptionField 
        description={description} 
        setDescription={setDescription} 
        ref={descriptionInputRef}
      />
    </Stack>
  );
};

export default TransferTransactionForm; 